import React, { useState, useEffect } from 'react';
import { Calendar, Upload, X, ImageIcon } from 'lucide-react';
import './MultiDayCalendar.css';

export default function MultiDayCalendar({
  selectedDates,
  onDatesChange,
  dailyDetails,
  onDailyDetailsChange,
  initialMonth
}) {
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (initialMonth) return new Date(initialMonth);
    if (selectedDates && selectedDates.length > 0) return new Date(selectedDates[0]);
    return new Date();
  });
  const [selectedDays, setSelectedDays] = useState(selectedDates || []);
  const [showImageModal, setShowImageModal] = useState(false);
  const [currentDayEdit, setCurrentDayEdit] = useState(null);

  // Sync selectedDays when parent updates dates (e.g. on edit load or reset).
  // Only navigate to the first selected month if we had NO dates before
  // (i.e., initial load from edit mode). This prevents the calendar from
  // jumping back to the first selected month on every date toggle.
  useEffect(() => {
    setSelectedDays(prev => {
      if (prev.length === 0 && selectedDates && selectedDates.length > 0) {
        setCurrentMonth(new Date(selectedDates[0]));
      }
      return selectedDates || [];
    });
  }, [selectedDates]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const isDateSelected = (date) => {
    return selectedDays.some(d =>
      d.getDate() === date.getDate() &&
      d.getMonth() === date.getMonth() &&
      d.getFullYear() === date.getFullYear()
    );
  };

  // Helper: local date to YYYY-MM-DD without UTC shift
  const toLocalDateStr = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const toggleDate = (date) => {
    const dateStr = toLocalDateStr(date);
    let newSelectedDays;

    if (isDateSelected(date)) {
      newSelectedDays = selectedDays.filter(d =>
        !(d.getDate() === date.getDate() &&
          d.getMonth() === date.getMonth() &&
          d.getFullYear() === date.getFullYear())
      );
      const newDailyDetails = { ...dailyDetails };
      delete newDailyDetails[dateStr];
      onDailyDetailsChange(newDailyDetails);
    } else {
      newSelectedDays = [...selectedDays, date];
    }

    setSelectedDays(newSelectedDays);
    onDatesChange(newSelectedDays);
  };

  const handlePreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const openImageModal = (date) => {
    setCurrentDayEdit(date);
    setShowImageModal(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const base64 = await convertToBase64(file);
      const dateStr = toLocalDateStr(currentDayEdit);
      const newDailyDetails = {
        ...dailyDetails,
        [dateStr]: {
          ...dailyDetails[dateStr],
          image: base64,
          date: dateStr
        }
      };
      onDailyDetailsChange(newDailyDetails);
    } catch (error) {
      console.error('Erro ao fazer upload da imagem:', error);
      alert('Erro ao fazer upload da imagem');
    }
  };

  const handleDescriptionChange = (description) => {
    const dateStr = toLocalDateStr(currentDayEdit);
    const newDailyDetails = {
      ...dailyDetails,
      [dateStr]: {
        ...dailyDetails[dateStr],
        description,
        date: dateStr
      }
    };
    onDailyDetailsChange(newDailyDetails);
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('pt-PT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const renderCalendar = () => {
    const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth);
    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      date.setHours(0, 0, 0, 0);
      const isPast = date < today;
      const selected = isDateSelected(date);
      const dateStr = toLocalDateStr(date);
      const hasDetails = dailyDetails[dateStr];

      days.push(
        <div
          key={day}
          className={`calendar-day ${selected ? 'selected' : ''} ${isPast ? 'past' : ''}`}
          onClick={() => !isPast && toggleDate(date)}
        >
          <span className="day-number">{day}</span>
          {selected && (
            <button
              className="add-image-btn"
              onClick={(e) => {
                e.stopPropagation();
                openImageModal(date);
              }}
            >
              {hasDetails?.image ? (
                <ImageIcon size={16} className="has-image" />
              ) : (
                <Upload size={16} />
              )}
            </button>
          )}
        </div>
      );
    }

    return days;
  };

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  return (
    <div className="multi-day-calendar">
      <div className="calendar-header">
        <button type="button" onClick={handlePreviousMonth} className="month-nav">
          ←
        </button>
        <h3 className="month-title">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        <button type="button" onClick={handleNextMonth} className="month-nav">
          →
        </button>
      </div>

      <div className="calendar-weekdays">
        {weekDays.map(day => (
          <div key={day} className="weekday">{day}</div>
        ))}
      </div>

      <div className="calendar-grid">
        {renderCalendar()}
      </div>

      <div className="selected-dates-summary">
        <h4>Dias Selecionados: {selectedDays.length}</h4>
        {selectedDays.length > 0 && (
          <div className="selected-dates-list">
            {selectedDays.sort((a, b) => a - b).map((date, index) => {
              const dateStr = toLocalDateStr(date);
              const details = dailyDetails[dateStr];
              return (
                <div key={index} className="selected-date-item">
                  <span>{formatDate(date)}</span>
                  {details?.image && <ImageIcon size={14} className="has-image-icon" />}
                  <button
                    type="button"
                    onClick={() => openImageModal(date)}
                    className="edit-day-btn"
                  >
                    Editar
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showImageModal && currentDayEdit && (
        <div className="image-modal-overlay" onClick={() => setShowImageModal(false)}>
          <div className="image-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Detalhes para {formatDate(currentDayEdit)}</h3>
              <button onClick={() => setShowImageModal(false)} className="close-modal">
                <X size={24} />
              </button>
            </div>

            <div className="modal-content">
              <div className="form-group">
                <label>Imagem do Dia</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="file-input"
                />
                {dailyDetails[toLocalDateStr(currentDayEdit)]?.image && (
                  <div className="image-preview">
                    <img
                      src={dailyDetails[toLocalDateStr(currentDayEdit)].image}
                      alt="Preview"
                    />
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Descrição do Dia</label>
                <textarea
                  value={dailyDetails[toLocalDateStr(currentDayEdit)]?.description || ''}
                  onChange={(e) => handleDescriptionChange(e.target.value)}
                  rows={4}
                  placeholder="Descreve as atividades deste dia..."
                  className="form-textarea"
                />
              </div>

              <button
                onClick={() => setShowImageModal(false)}
                className="save-btn"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}